/**
 * Top navigation — DESIGN_GUIDELINES.md §8.1/§8.2.
 *
 * Desktop: a rounded pill bar inside the content card, active item a solid ink pill.
 * Mobile: the same items become a bottom tab bar.
 *
 * Note the active pill is `--ff-ink`, NOT the brand green: white text on green-500 is 2.85:1 and
 * fails WCAG AA (§3.2).
 */

import { NavLink } from 'react-router-dom';
import { Avatar, Button, Group, Menu, Text } from '@mantine/core';
import type { SessionUser } from '../lib/api';
import { Logo } from './Logo';

const NAV_ITEMS = [
  { to: '/', label: 'Recipes', end: true },
  { to: '/import', label: 'Import', end: false },
  { to: '/lists', label: 'Lists', end: false },
];

export function AppNav({
  user,
  onSignOut,
}: {
  user: SessionUser;
  onSignOut: () => void;
}) {
  return (
    <>
      <Group justify="space-between" wrap="nowrap" mb="lg">
        <Group gap="sm" wrap="nowrap">
          <Logo />
          {/* Hidden on the smallest screens where the nav below carries the identity. */}
          <Text fw={700} style={{ fontSize: 17 }} visibleFrom="sm">
            Recipe Planner
          </Text>
        </Group>

        <Group gap={4} wrap="nowrap" visibleFrom="sm">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} style={{ textDecoration: 'none' }}>
              {({ isActive }) => (
                <Button
                  component="span"
                  variant={isActive ? 'filled' : 'subtle'}
                  color={isActive ? 'dark' : 'gray'}
                  size="compact-md"
                  radius="xl"
                  styles={{
                    root: {
                      color: isActive ? undefined : 'var(--ff-muted)',
                      fontWeight: 600,
                    },
                  }}
                >
                  {item.label}
                </Button>
              )}
            </NavLink>
          ))}
        </Group>

        <Menu position="bottom-end" withArrow>
          <Menu.Target>
            <Button variant="subtle" color="gray" px="xs" aria-label="Account menu">
              <Group gap="xs" wrap="nowrap">
                <Avatar size={28} radius="xl" color="hfPurple">
                  {user.displayName.slice(0, 1).toUpperCase()}
                </Avatar>
                <Text size="sm" fw={600} c="var(--ff-ink)" visibleFrom="sm">
                  {user.displayName}
                </Text>
              </Group>
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>{user.email}</Menu.Label>
            <Menu.Item onClick={onSignOut}>Sign out</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      {/* Mobile: bottom tab bar. Primary destinations stay visible rather than hidden in a drawer. */}
      <Group
        justify="center"
        gap="lg"
        hiddenFrom="sm"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          padding: '10px 0 calc(10px + env(safe-area-inset-bottom))',
          background: 'var(--ff-white)',
          borderTop: '1px solid var(--ff-border)',
          zIndex: 100,
        }}
      >
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            style={({ isActive }) => ({
              textDecoration: 'none',
              color: isActive ? 'var(--ff-ink)' : 'var(--ff-muted)',
              fontWeight: 600,
              fontSize: 13,
              minWidth: 64,
              textAlign: 'center',
            })}
          >
            {item.label}
          </NavLink>
        ))}
      </Group>
    </>
  );
}
